import { StaticModal, createModal } from '@nexelem/modals';
import { useCustomTranslation } from '../../../../../hooks';
import { Button } from '@nexelem/components';
import { FormDateTimeInput } from '@nexelem/form-components';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSaveSimulationFreezingBoundaryMutation, useSimulationId } from '../hooks';
import { Trans } from 'react-i18next';
import { SimulationMetadataView } from '@nexelem/graphne-api';

const freezeRangeSchema = z.object({
  dateTime: z.date(),
});

type FreezeRangeFormValues = z.infer<typeof freezeRangeSchema>;

type FreezeRangeModalProps = {
  simulationMeta: SimulationMetadataView;
};

const FreezeRangeModal = ({ simulationMeta }: FreezeRangeModalProps) => {
  const { t, tWrapper } = useCustomTranslation('products.productionPlan');
  const { mutate, isPending } = useSaveSimulationFreezingBoundaryMutation();
  const { id } = useSimulationId();

  const methods = useForm({
    resolver: zodResolver(freezeRangeSchema),
    defaultValues: {
      dateTime: new Date(simulationMeta.freezingBoundary),
    },
  });

  const onSubmit = (data: FreezeRangeFormValues) => {
    mutate(
      {
        simulationId: id,
        freezingBoundary: data.dateTime.toISOString(),
      },
      {
        onSuccess: () => {
          closeFreezeRangeModal();
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <StaticModal
        component="form"
        maxWidth="lg"
        onClose={closeFreezeRangeModal}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <StaticModal.Header withCloseButton>{tWrapper('freezeRange')}</StaticModal.Header>
        <StaticModal.Content className="scroll-stable pr-2">
          <div className="mb-8 text-s18">
            <Trans components={{ b: <b /> }}>{tWrapper('selectFreezeRangeDateTime')}</Trans>
          </div>
          <FormDateTimeInput required name="dateTime" />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeFreezeRangeModal}>
            {t('common.cancel')}
          </Button>
          <Button isLoading={isPending} type="submit" variant="contained">
            {t('common.save')}
          </Button>
        </StaticModal.Footer>
      </StaticModal>
    </FormProvider>
  );
};

export const { openFreezeRangeModal, closeFreezeRangeModal } = createModal(FreezeRangeModal, {
  name: 'FreezeRangeModal',
});
